import axios from 'axios';
import { API_URL } from '../config';

const BASE_URL = '/mix-deliveries';

export async function getLatestMixStockSnapshot() {
  const response = await axios.get(`${API_URL}${BASE_URL}/stock/latest`, {
    withCredentials: true,
  });

  return response.data;
}

export async function importMixStock(file) {
  const formData = new FormData();

  formData.append('file', file);

  const response = await axios.post(
    `${API_URL}${BASE_URL}/stock/import`,
    formData,
    {
      withCredentials: true,
    },
  );

  return response.data;
}

export async function importMixDeliveries(file) {
  const formData = new FormData();

  formData.append('file', file);
  formData.append('confirmFullImport', 'true');

  const response = await axios.post(`${API_URL}${BASE_URL}/imports`, formData, {
    withCredentials: true,
  });

  const importId = response.data?.importId ?? response.data?.id;

  if (!importId) {
    throw new Error('Backend nie zwrócił identyfikatora importu.');
  }

  return getMixImport(importId);
}

export async function importMixTransports(file) {
  const formData = new FormData();

  formData.append('file', file);

  const response = await axios.post(
    `${API_URL}${BASE_URL}/import/transport`,
    formData,
    {
      withCredentials: true,
    },
  );

  return response.data;
}

export async function getLatestMixImport() {
  const response = await axios.get(`${API_URL}${BASE_URL}/imports/latest`, {
    withCredentials: true,
  });

  return response.data;
}

export async function getMixImport(importId) {
  const response = await axios.get(
    `${API_URL}${BASE_URL}/imports/${importId}`,
    {
      withCredentials: true,
    },
  );

  return response.data;
}

export async function getMixMissingProducts(importId) {
  const response = await axios.get(
    `${API_URL}${BASE_URL}/imports/${importId}/missing-products`,
    {
      withCredentials: true,
    },
  );

  return response.data;
}

export async function resumeMixImport(importId) {
  const response = await axios.post(
    `${API_URL}${BASE_URL}/imports/${importId}/resume`,
    {},
    {
      withCredentials: true,
    },
  );

  const returnedImportId =
    response.data?.importId ?? response.data?.id ?? importId;

  return getMixImport(returnedImportId);
}

export async function downloadMixPlanningFile(importId) {
  const response = await axios.get(
    `${API_URL}${BASE_URL}/imports/${importId}/planning-file`,
    {
      responseType: 'blob',
      withCredentials: true,
    },
  );

  downloadBlobResponse(response, 'planowanie-miksy-8AAE.xlsx');
}

export async function getMixDashboard() {
  const response = await axios.get(`${API_URL}${BASE_URL}/dashboard`, {
    withCredentials: true,
  });

  return response.data;
}

export async function getMixDeliveries(params = {}) {
  const response = await axios.get(`${API_URL}${BASE_URL}`, {
    params: cleanParams(params),
    withCredentials: true,
  });

  return response.data;
}

export async function getMixDelivery(deliveryId) {
  const response = await axios.get(`${API_URL}${BASE_URL}/${deliveryId}`, {
    withCredentials: true,
  });

  return response.data;
}

export async function updateMixDeliveryStatus(deliveryId, status, comment) {
  const response = await axios.patch(
    `${API_URL}${BASE_URL}/${deliveryId}/status`,
    {
      status,
      comment: comment?.trim() || undefined,
    },
    {
      withCredentials: true,
    },
  );

  return response.data;
}

export async function confirmMixDeliveryReview(deliveryId, comment = '') {
  const response = await axios.patch(
    `${API_URL}${BASE_URL}/${deliveryId}/review/confirm`,
    {
      comment: comment.trim() || undefined,
    },
    {
      withCredentials: true,
    },
  );

  return response.data;
}

// export async function getMixStockSummary() {
//   const response = await axios.get(`${API_URL}${BASE_URL}/stock-summary`, {
//     withCredentials: true,
//   });

//   return response.data;
// }

export const getMixStockSummary = async ({ deliveryIds = [] } = {}) => {
  const response = await axios.get(`${API_URL}${BASE_URL}/stock-summary`, {
    params:
      deliveryIds.length > 0
        ? {
            deliveryIds: deliveryIds.join(','),
          }
        : undefined,
    withCredentials: true,
  });

  return response.data;
};

// export async function downloadMixStockSummaryFile() {
//   const response = await axios.get(`${API_URL}${BASE_URL}/stock-summary/file`, {
//     responseType: 'blob',
//     withCredentials: true,
//   });

//   downloadBlobResponse(response, 'uzupelnienia-0D20.xlsx');
// }

export const downloadMixStockSummaryFile = async ({
  deliveryIds = [],
} = {}) => {
  const response = await axios.get(`${API_URL}${BASE_URL}/stock-summary/file`, {
    responseType: 'blob',

    params:
      deliveryIds.length > 0
        ? {
            deliveryIds: deliveryIds.join(','),
          }
        : undefined,
    withCredentials: true,
  });

  downloadBlobResponse(response, 'uzupelnienie_0D20.xlsx');
};

export async function getMixRecipientConfigs() {
  const response = await axios.get(`${API_URL}${BASE_URL}/recipient-configs`, {
    withCredentials: true,
  });

  return response.data;
}

export async function upsertMixRecipientConfig(recipientNumber, data) {
  const normalizedRecipientNumber = String(recipientNumber ?? '').trim();

  if (!normalizedRecipientNumber) {
    throw new Error('Numer odbiorcy jest wymagany.');
  }

  const response = await axios.put(
    `${API_URL}${BASE_URL}/recipient-configs/${encodeURIComponent(
      normalizedRecipientNumber,
    )}`,
    {
      recipientName: data.recipientName?.trim() || undefined,
      sandwichingRequired: Boolean(data.sandwichingRequired),
      isActive: Boolean(data.isActive),
    },
    {
      withCredentials: true,
    },
  );

  return response.data;
}

/**
 * Historia fizycznie przygotowanych miksów.
 *
 * Obsługiwane parametry:
 * - page
 * - limit
 * - preparedFrom
 * - preparedTo
 * - search
 * - preparedById
 * - sandwichingRequired
 * - changedAfterPreparation
 * - requiresReview
 * - isActive
 */
export async function getPreparedMixesReport(params = {}) {
  const response = await axios.get(`${API_URL}${BASE_URL}/reports/prepared`, {
    params: cleanParams(params),
    withCredentials: true,
  });

  return response.data;
}

/**
 * Eksport historii przygotowanych miksów do XLSX.
 *
 * Parametry powinny być takie same jak dla
 * getPreparedMixesReport().
 */
export async function downloadPreparedMixesReport(params = {}) {
  const response = await axios.get(
    `${API_URL}${BASE_URL}/reports/prepared/file`,
    {
      params: cleanParams(params),
      responseType: 'blob',
      withCredentials: true,
    },
  );

  downloadBlobResponse(response, 'historia-przygotowanych-miksow.xlsx');
}

/**
 * Raport częstotliwości występowania materiałów
 * w fizycznie przygotowanych pozycjach miksowych.
 *
 * Obsługiwane parametry:
 * - page
 * - limit
 * - preparedFrom
 * - preparedTo
 * - materialSearch
 * - recipientSearch
 * - preparedById
 * - sandwichingRequired
 */
export async function getMaterialFrequencyReport(params = {}) {
  const response = await axios.get(
    `${API_URL}${BASE_URL}/reports/material-frequency`,
    {
      params: cleanParams(params),
      withCredentials: true,
    },
  );

  return response.data;
}

export async function downloadMaterialFrequencyReport(params = {}) {
  const response = await axios.get(
    `${API_URL}${BASE_URL}/reports/material-frequency/file`,
    {
      params: cleanParams(params),
      responseType: 'blob',
      withCredentials: true,
    },
  );

  downloadBlobResponse(response, 'czestotliwosc-materialow-miksowych.xlsx');
}

export function getApiErrorMessage(error) {
  const responseData = error?.response?.data;

  const message = responseData?.message;

  if (Array.isArray(message)) {
    return message.join(', ');
  }

  if (typeof message === 'string') {
    return message;
  }

  if (Array.isArray(responseData?.errors) && responseData.errors.length > 0) {
    return responseData.errors.join(', ');
  }

  if (typeof error?.message === 'string') {
    return error.message;
  }

  return 'Wystąpił nieoczekiwany błąd.';
}

function cleanParams(params = {}) {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => {
      return value !== undefined && value !== null && value !== '';
    }),
  );
}

function downloadBlobResponse(response, fallbackFileName) {
  const contentDisposition = response.headers?.['content-disposition'];

  const fileName =
    getFileNameFromHeader(contentDisposition) ?? fallbackFileName;

  const blobUrl = window.URL.createObjectURL(response.data);

  const link = document.createElement('a');

  link.href = blobUrl;
  link.download = fileName;

  document.body.appendChild(link);

  link.click();
  link.remove();

  window.URL.revokeObjectURL(blobUrl);
}

function getFileNameFromHeader(contentDisposition) {
  if (!contentDisposition) {
    return null;
  }

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);

  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const standardMatch = contentDisposition.match(/filename="?([^";]+)"?/i);

  return standardMatch?.[1] ?? null;
}
export const getUnusedPickingStockReport = async () => {
  const response = await axios.get(`${API_URL}${BASE_URL}/stock-unused`, {
    withCredentials: true,
  });

  return response.data;
};

export const downloadUnusedPickingStockReport = async () => {
  const response = await axios.get(`${API_URL}${BASE_URL}/stock-unused/file`, {
    responseType: 'blob',
    withCredentials: true,
  });

  downloadBlobResponse(response, 'materialy_0D20_do_0D30.xlsx');
};

export const getMixConsolidationReport = async () => {
  const response = await axios.get(
    `${API_URL}${BASE_URL}/consolidation/report`,
    {
      withCredentials: true,
    },
  );

  return response.data;
};

export const downloadMixConsolidationReport = async () => {
  const response = await axios.get(
    `${API_URL}${BASE_URL}/consolidation/report/file`,
    {
      responseType: 'blob',
      withCredentials: true,
    },
  );

  downloadBlobResponse(response, 'konsolidacja_0D20.xlsx');
};

export const getMixConsolidationConfig = async () => {
  const response = await axios.get(
    `${API_URL}${BASE_URL}/consolidation/config`,
    {
      withCredentials: true,
    },
  );

  return response.data;
};

export const updateMixConsolidationConfig = async (tolerancePercent) => {
  const response = await axios.patch(
    `${API_URL}${BASE_URL}/consolidation/config`,
    {
      tolerancePercent,
    },
    {
      withCredentials: true,
    },
  );

  return response.data;
};
